// Message mappers - transform backend-specific message data to contract types
import type { DirectorNote, Message, MessageAlternative } from '@/types/domain'
import type { MessageResponse, MessageAlternativesResponse, MessageAlternativeResponse, MessageDeleteResponse } from '@/types/api'
import { normalizeDate } from './normalize'
import type { MessagePhase } from '@/lib/messageSlots'

function lifecyclePhase(source: { status?: unknown; is_streaming?: unknown; is_complete?: unknown }): MessagePhase {
  if (source.status === 'error') return 'error'
  if (source.status === 'thinking') return 'thinking'
  if (source.status === 'streaming' || source.is_streaming) return 'streaming'
  return 'complete'
}

interface RawDirectorNote {
  id: string | number;
  message_id: string | number;
  visibility_count?: number | null;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

interface RawMessage {
  id: string | number;
  conversation_id: string | number;
  director_note?: RawDirectorNote | RawDirectorNote[] | null;
  active?: RawActiveMessage | RawActiveMessage[] | null;
  created_at?: string;
  updated_at?: string;
  t?: string;
  [key: string]: unknown;
}

interface RawActiveMessage {
  content?: string | null;
  stream_id?: string | null;
  is_streaming?: boolean | null;
  is_complete?: boolean | null;
  [key: string]: unknown;
}

interface RawMessageAlternative {
  id: string | number;
  message_id: string | number;
  created_at?: string;
  updated_at?: string;
  t?: string;
  status?: string | null;
  is_streaming?: boolean | null;
  is_complete?: boolean | null;
  [key: string]: unknown;
}

export function mapDirectorNoteFromSupabase(raw: RawDirectorNote | null | undefined): DirectorNote | null {
  if (!raw) return null;

  return {
    ...raw,
    id: Number(raw.id),
    message_id: Number(raw.message_id),
    visibility_count: raw.visibility_count === null || raw.visibility_count === undefined
      ? null
      : Number(raw.visibility_count),
    ...(raw.created_at && { created_at: normalizeDate(raw.created_at) }),
    ...(raw.updated_at && { updated_at: normalizeDate(raw.updated_at) }),
  } as DirectorNote;
}

export function mapMessageFromSupabase(raw: RawMessage): Message {
  const { active: rawActive, ...messageRaw } = raw;
  const active = Array.isArray(rawActive) ? rawActive[0] : rawActive;
  const activeProjection = active
    ? {
        content: active.content,
        stream_id: active.stream_id,
        is_streaming: active.is_streaming,
        is_complete: active.is_complete,
      }
    : {};

  return {
    ...messageRaw,
    ...activeProjection,
    id: Number(raw.id),
    conversation_id: Number(raw.conversation_id),
    director_note: mapDirectorNoteFromSupabase(Array.isArray(raw.director_note) ? raw.director_note[0] : raw.director_note),
    phase: lifecyclePhase({ ...messageRaw, ...activeProjection }),
    ...(raw.created_at && { created_at: normalizeDate(raw.created_at) }),
    ...(raw.updated_at && { updated_at: normalizeDate(raw.updated_at) }),
    ...(raw.t && { t: normalizeDate(raw.t) }),
  } as unknown as Message;
}

export function mapMessagesFromSupabase(rawList: RawMessage[]): Message[] {
  return rawList?.map(mapMessageFromSupabase) || [];
}

export function mapMessageResponseFromSupabase(raw: { message: RawMessage }): MessageResponse {
  return {
    message: mapMessageFromSupabase(raw.message),
  };
}

export function mapMessageAlternativeFromSupabase(raw: RawMessageAlternative): MessageAlternative {
  return {
    ...raw,
    id: Number(raw.id),
    message_id: Number(raw.message_id),
    phase: lifecyclePhase(raw),
    ...(raw.created_at && { created_at: normalizeDate(raw.created_at) }),
    ...(raw.updated_at && { updated_at: normalizeDate(raw.updated_at) }),
    ...(raw.t && { t: normalizeDate(raw.t) }),
  } as unknown as MessageAlternative;
}

export function mapMessageAlternativesFromSupabase(rawList: RawMessageAlternative[]): MessageAlternative[] {
  return rawList?.map(mapMessageAlternativeFromSupabase) || [];
}

export function mapMessageAlternativesResponseFromSupabase(raw: { alternatives: RawMessageAlternative[] }): MessageAlternativesResponse {
  return {
    alternatives: mapMessageAlternativesFromSupabase(raw.alternatives),
    active_variant_id: null,
  };
}

export function mapMessageAlternativeResponseFromSupabase(raw: { alternative: RawMessageAlternative }): MessageAlternativeResponse {
  return {
    alternative: mapMessageAlternativeFromSupabase(raw.alternative),
  };
}

export function mapMessageDeleteResponseFromSupabase(raw: { message?: string }): MessageDeleteResponse {
  return {
    message: raw.message || 'Message operation completed successfully',
  };
}
